// Copied from https://github.com/snamoah/react-datetime-range-picker

/* eslint 'react/sort-comp': off, 'react/jsx-no-bind': off */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Datetime from 'react-datetime';

// Material UI
import { withStyles } from '@material-ui/core/styles';

// import style
import './styles.css';

// Utils
import clsx from 'clsx';

const styles = ({ palette, spacing, shape }) => {
  const { hover } = palette.action;
  const { secondary: secondaryText } = palette.text;

  return {
    root: {
      '& .rdtPicker': {
        background: palette.background.paper,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',

        '& td.rdtMonth:hover': {
          background: hover
        },
        '& td.rdtYear:hover': {
          background: hover
        },
        '& td.rdtDay:hover': {
          background: hover
        },
        '& td.rdtHour:hover': {
          background: hover
        },
        '& td.rdtMinute:hover': {
          background: hover
        },
        '& td.rdtSecond:hover': {
          background: hover
        },
        '& .rdtTimeToggle:hover': {
          background: hover
        },

        '& td.rdtOld': {
          color: secondaryText
        },
        '& td span.rdtOld': {
          color: secondaryText
        },
        '& td.rdtNew': {
          color: secondaryText
        },
        '& td.rdtDisabled': {
          color: secondaryText,

          '&:hover': {
            color: secondaryText
          }
        },
        '& td span.rdtDisabled': {
          color: secondaryText,

          '&:hover': {
            color: secondaryText
          }
        },
        '& th.rdtDisabled': {
          color: secondaryText,

          '&:hover': {
            color: secondaryText
          }
        },

        '& td.rdtToday:before': {
          borderBottomColor: palette.primary.main
        },

        '& td.rdtActive': {
          backgroundColor: `${palette.primary.main} !important`,
          color: `${palette.primary.contrastText} !important`,
          textShadow: '0 -1px 0 rgba(0, 0, 0, 0.25) !important',

          '&.rdtToday:before': {
            borderBottomColor: palette.primary.contrastText
          }
        },

        '& .rdtDay.in-selecting-range': {
          backgroundColor: `${palette.primary.light} !important`,
          color: `${palette.primary.contrastText} !important`
        }
      }
    },
    datePicker: {
      display: 'inline-block',
      margin: spacing(0.5),

      '& input': {
        background: 'transparent !important',
        padding: '7px 15px',
        color: palette.text.primary,
        borderColor: `${palette.type === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'} !important`,
        borderRadius: shape.borderRadius,

        '&::placeholder': {
          color: palette.text.secondary
        },

        '&:focus': {
          padding: '6px 14px',
          outline: 'none',
          borderColor: `${palette.primary.main} !important`,
          borderWidth: '2px !important'
        },

        '&:hover': {
          borderColor: `${palette.text.primary} !important`
        }
      }
    }
  };
};

class DatetimeRangePicker extends Component {
  constructor(props) {
    super(props);

    this.state = {
      start: null,
      end: null,
      startDate: null,
      endDate: null
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    return nextProps.startDate === prevState.startDate && nextProps.endDate === prevState.endDate
      ? {}
      : {
          start: moment(nextProps.startDate) || moment(),
          end: moment(nextProps.endDate) || moment(),
          startDate: nextProps.startDate,
          endDate: nextProps.endDate
        };
  }

  getInputProps(extraProps) {
    const inputProps = {
      ...this.props.inputProps,
      ...extraProps
    };
    const inputReadOnlyStyle = {
      cursor: 'pointer',
      backgroundColor: 'white',
      border: '1px solid #e2e2e2'
    };

    return this.props.input
      ? inputProps
      : {
          input: true,
          inputProps: {
            ...inputProps, // merge inputProps with default
            readOnly: true,
            style: inputReadOnlyStyle
          }
        };
  }

  propsToPass() {
    return {
      end: this.state.end.toDate(),
      start: this.state.start.toDate()
    };
  }

  calcBaseProps() {
    return {
      utc: this.props.utc,
      locale: this.props.locale,
      input: !this.props.inline,
      viewMode: this.props.viewMode,
      dateFormat: this.props.dateFormat,
      timeFormat: this.props.timeFormat,
      closeOnTab: this.props.closeOnTab,
      className: clsx(this.props.classes.datePicker, this.props.pickerClassName),
      closeOnSelect: this.props.closeOnSelect
    };
  }

  calcStartTimeProps() {
    const baseProps = this.calcBaseProps();
    const inputProps = this.getInputProps(this.props.startInputProps);

    return {
      ...baseProps,
      ...inputProps,
      value: this.state.start,
      onClose: this.props.onStartDateBlur,
      onOpen: this.props.onStartDateFocus,
      timeConstraints: this.props.startTimeConstraints
    };
  }

  calcEndTimeProps() {
    const baseProps = this.calcBaseProps();
    const inputProps = this.getInputProps(this.props.endInputProps);

    return {
      ...baseProps,
      ...inputProps,
      onClose: this.props.onEndDateBlur,
      value: this.state.end,
      onOpen: this.props.onEndDateFocus,
      timeConstraints: this.props.endTimeConstraints
    };
  }

  validateMinDate(date) {
    return this.state.start.isSameOrBefore(date, 'day');
  }

  isValidEndDate(currentDate, selectedDate) {
    return this.validateMinDate(currentDate) && this.props.isValidEndDate(currentDate, selectedDate);
  }

  onStartDateChange(date) {
    if (typeof date === 'string') {
      return;
    }

    const options = {
      start: date
    };

    if (this.state.end.isBefore(date)) {
      options.end = date.clone().add(1, 'h');
    }

    this.setState(options, () => {
      this.props.onChange(this.propsToPass());
      this.props.onStartDateChange(this.propsToPass().start);
    });
  }

  onEndDateChange(date) {
    if (typeof date === 'string') {
      return;
    }

    const options = {
      end: date
    };

    if (this.state.start.isAfter(date)) {
      options.start = date.clone().subtract(1, 'h');
    }

    this.setState(options, () => {
      this.props.onChange(this.propsToPass());
      this.props.onEndDateChange(this.propsToPass().end);
    });
  }

  onFocus() {
    this.props.onFocus();
  }

  onBlur() {
    this.props.onBlur(this.propsToPass());
  }

  renderDay(props, currentDate) {
    const { start, end } = this.state;
    const { className, ...rest } = props;
    const date = moment(props.key, 'M_D');

    // style all dates in range
    let classes = date.isBetween(start, end, 'day') ? `${props.className} in-selecting-range` : props.className;

    // add rdtActive to selected startdate and endDate in pickers
    classes = date.isSame(start, 'day') || date.isSame(end, 'day') ? `${classes} rdtActive` : classes;

    return (
      <td {...rest} className={classes}>
        {currentDate.date()}
      </td>
    );
  }
  render() {
    const startProps = this.calcStartTimeProps();
    const endProps = this.calcEndTimeProps();

    return (
      <div
        className={clsx(this.props.classes.root, this.props.className)}
        onFocus={this.onFocus.bind(this)}
        onBlur={this.onBlur.bind(this)}
      >
        <Datetime
          {...startProps}
          isValidDate={this.props.isValidStartDate}
          onChange={this.onStartDateChange.bind(this)}
          renderDay={this.renderDay.bind(this)}
        />

        <Datetime
          {...endProps}
          isValidDate={this.isValidEndDate.bind(this)}
          onChange={this.onEndDateChange.bind(this)}
          renderDay={this.renderDay.bind(this)}
        />
      </div>
    );
  }
}

DatetimeRangePicker.defaultProps = {
  utc: false,
  locale: null,
  input: false, // This defines whether or not to to edit date manually via input
  inline: false, // This defines whether or not to show input field
  className: '',
  viewMode: 'days',
  dateFormat: true,
  timeFormat: true,
  closeOnTab: true,
  onBlur: () => {},
  onFocus: () => {},
  onChange: () => {},
  pickerClassName: '',
  endDate: new Date(),
  closeOnSelect: false,
  inputProps: undefined,
  startDate: new Date(),
  onEndDateBlur: () => {},
  endTimeConstraints: {},
  onEndDateFocus: () => {},
  isValidStartDate: () => true,
  isValidEndDate: () => true,
  onStartDateBlur: () => {},
  onEndDateChange: () => {}, // This is called after onChange
  onStartDateFocus: () => {},
  startTimeConstraints: {},
  onStartDateChange: () => {} // This is called after onChange
};

DatetimeRangePicker.propTypes = {
  utc: PropTypes.bool,
  input: PropTypes.bool,
  inline: PropTypes.bool,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  locale: PropTypes.string,
  onChange: PropTypes.func,
  viewMode: PropTypes.oneOf(['years', 'months', 'days', 'time']),
  closeOnTab: PropTypes.bool,
  className: PropTypes.string,
  inputProps: PropTypes.object, // eslint-disable-line
  startInputProps: PropTypes.object, // eslint-disable-line
  endInputProps: PropTypes.object, // eslint-disable-line
  closeOnSelect: PropTypes.bool,
  isValidEndDate: PropTypes.func,
  onEndDateBlur: PropTypes.func,
  onEndDateFocus: PropTypes.func,
  onEndDateChange: PropTypes.func,
  onStartDateBlur: PropTypes.func,
  isValidStartDate: PropTypes.func,
  onStartDateFocus: PropTypes.func,
  onStartDateChange: PropTypes.func,
  pickerClassName: PropTypes.string,
  defaultEndDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.instanceOf(Date), PropTypes.string]),
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.instanceOf(Date), PropTypes.string]),
  endTimeConstraints: PropTypes.object, // eslint-disable-line
  startDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.instanceOf(Date), PropTypes.string]),
  defaultStartDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.instanceOf(Date), PropTypes.string]),
  startTimeConstraints: PropTypes.object, // eslint-disable-line
  dateFormat: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  timeFormat: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(DatetimeRangePicker);
